import { Trans, useLingui } from "@lingui/react/macro";
import { updateRequiredCompetences } from "@/api/Activities";
import { FilledButton } from "@medoma/components/Button/Button";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import Form from "@/components/Form/Form";
import { Loading } from "@medoma/components/Loading/Loading";
import { deducedError } from "@/Utils/ErrorUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FormProvider, useForm } from "react-hook-form";
import type { IMedicalCompetence } from "@medoma/models/shifts";
import {
  CompetencePicker,
  type ICompetencePickerFields,
} from "@/forms/AddActivity/CompetencePicker";

export const EditActivityRequiredCompetences = ({
  currentRequiredCompetences,
  isRequired,
  onSuccess,
  activityId,
}: {
  currentRequiredCompetences: IMedicalCompetence[];
  isRequired: boolean;
  onSuccess: () => void;
  activityId: string;
}) => {
  const { t } = useLingui();
  const methods = useForm<ICompetencePickerFields>({
    defaultValues: {
      requiredCompetences: currentRequiredCompetences,
    },
  });

  const {
    formState: { errors, isDirty },
    getValues,
    handleSubmit,
    reset,
    setError,
  } = methods;

  const queryClient = useQueryClient();
  const { mutate, isPending, isSuccess } = useMutation({
    mutationFn: (requiredCompetences: IMedicalCompetence[]) =>
      updateRequiredCompetences(activityId, requiredCompetences),
    onError: (error) => {
      setError("root.server", {
        message: deducedError(error),
      });
      reset(getValues(), {
        keepErrors: true,
        keepIsSubmitted: true,
        keepTouched: true,
        keepIsValid: true,
        keepSubmitCount: true,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries();
      onSuccess();
    },
  });

  return (
    <FormProvider {...methods}>
      <Form
        onSubmit={handleSubmit((formData) => {
          mutate(formData.requiredCompetences);
        })}
      >
        {isPending || isSuccess ? (
          <Loading message={t`Ändrar kompetensbehov`} />
        ) : (
          <>
            {errors.root?.server?.message && !isDirty ? (
              <ErrorMessage message={errors.root.server.message} />
            ) : null}
            <Form.Row>
              <CompetencePicker isRequired={isRequired} />
            </Form.Row>
            {isDirty ? (
              <FilledButton type="submit">
                <Trans>Spara ändringar</Trans>
              </FilledButton>
            ) : null}
          </>
        )}
      </Form>
    </FormProvider>
  );
};
